import React, { useState } from 'react';
import { RevoltTable, RevoltTableCell } from '../../../components';
import { RevoltSwitch } from '../../../components/RevoltSwitch';
import DropdownSelect from '../../../components/DropdownSelect';
import { ModelCapabilities } from '../../../components/ModelCapabilities';

export function ModelsCard({ user }) {
  // Search state
  const [searchText, setSearchText] = useState('');

  // Filter state
  const [capabilityFilters, setCapabilityFilters] = useState({});

  // Sorting state
  const [sortBy, setSortBy] = useState('favorite'); // 'none', 'context', 'freeTier', 'name', 'provider', 'favorite', 'priceInput', 'priceOutput', 'priceBoth'
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc', 'desc'

  // Favorite state (modelId -> boolean)
  const [favoritedModels, setFavoritedModels] = useState({});

  // Loading state for favorite updates (modelId -> boolean)
  const [updatingFavorites, setUpdatingFavorites] = useState({});


  // Fetch user model settings (including favorites)
  const fetchUserModelSettings = React.useCallback(async () => {
    try {
      const response = await fetch('/api/account/models', {
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Extract favorite status from model settings
        const favorites = {};
        Object.entries(data.models || {}).forEach(([modelId, modelData]) => {
          if (modelData && typeof modelData.favorite === 'boolean') {
            favorites[modelId] = modelData.favorite;
          }
        });
        setFavoritedModels(favorites);
      }
    } catch (error) {
      console.error('Failed to fetch user model settings:', error);
      // Keep existing state on error
    }
  }, []);

  // Update model favorite status
  const updateModelFavorite = React.useCallback(async (modelId, isFavorite) => {
    // Set loading state for this specific model
    setUpdatingFavorites(prev => ({ ...prev, [modelId]: true }));

    try {
      const response = await fetch(`/api/account/models/${encodeURIComponent(modelId)}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          data: { favorite: isFavorite }
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data?.error || 'Failed to update favorite');
      }

      // Success - keep the optimistic update
      setFavoritedModels(prev => ({
        ...prev,
        [modelId]: isFavorite
      }));
    } catch (error) {
      console.error('Failed to update model favorite:', error);
      // Revert optimistic update on failure
      setFavoritedModels(prev => ({
        ...prev,
        [modelId]: !isFavorite  // Revert to opposite of what we tried to set
      }));
      // Could show user error message here
      throw error;
    } finally {
      // Clear loading state regardless of success/failure
      setUpdatingFavorites(prev => ({ ...prev, [modelId]: false }));
    }
  }, []);

  // Models data state
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter and sort models based on search, filter, and sorting criteria
  const getFilteredModels = React.useCallback(() => {
    let filtered = models.filter(model => {
      // Exclude special OpenRouter routing models
      const modelId = model.id || '';
      const modelName = (model.name || '').toLowerCase();
      if (modelId.includes('openrouter/auto') ||
          modelId.includes('openrouter/bodybuilder') ||
          modelName.includes('bodybuilder') ||
          modelName.includes('auto router')) {
        return false;
      }

      // Text search filter (name and provider)
      if (searchText.trim()) {
        const searchLower = searchText.toLowerCase();
        const name = (model.name || model.id || '').toLowerCase();
        const provider = (model.provider || '').toLowerCase();

        if (!name.includes(searchLower) && !provider.includes(searchLower)) {
          return false;
        }
      }

      // Capabilities filter
      const enabledCapabilityFilters = Object.entries(capabilityFilters).filter(([_, config]) => config.enabled);
      if (enabledCapabilityFilters.length > 0) {
        for (const [capability, config] of enabledCapabilityFilters) {
          const modelCapabilities = model.detectedCapabilities || [];
          const hasCapability = modelCapabilities.some(cap =>
            cap.toLowerCase().includes(capability.toLowerCase())
          );

          if (config.mode === 'include' && !hasCapability) {
            return false; // Model must have this capability but doesn't
          }
          if (config.mode === 'exclude' && hasCapability) {
            return false; // Model must NOT have this capability but does
          }
        }
      }


      return true;
    });

    // Apply sorting
    if (sortBy !== 'none') {
      filtered.sort((a, b) => {
        let aValue, bValue;

        switch (sortBy) {
          case 'name':
            aValue = (a.name || a.id || '').toLowerCase();
            bValue = (b.name || b.id || '').toLowerCase();
            break;
          case 'provider':
            aValue = (a.provider || '').toLowerCase();
            bValue = (b.provider || '').toLowerCase();
            break;
          case 'context':
            aValue = a.context_length || 0;
            bValue = b.context_length || 0;
            break;
          case 'freeTier':
            aValue = a.isFreeTier ? 1 : 0; // Free = 1, Paid = 0
            bValue = b.isFreeTier ? 1 : 0;
            break;
          case 'favorite':
            aValue = favoritedModels[a.id] ? 1 : 0; // Favorite = 1, Not favorite = 0
            bValue = favoritedModels[b.id] ? 1 : 0;
            break;
          case 'priceInput':
            aValue = (a.pricingNumeric?.prompt || 0);
            bValue = (b.pricingNumeric?.prompt || 0);
            break;
          case 'priceOutput':
            aValue = (a.pricingNumeric?.completion || 0);
            bValue = (b.pricingNumeric?.completion || 0);
            break;
          case 'priceBoth':
            // Average of input and output prices
            aValue = ((a.pricingNumeric?.prompt || 0) + (a.pricingNumeric?.completion || 0)) / 2;
            bValue = ((b.pricingNumeric?.prompt || 0) + (b.pricingNumeric?.completion || 0)) / 2;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [models, searchText, capabilityFilters, sortBy, sortDirection, favoritedModels]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const filteredModels = getFilteredModels();
  const totalPages = Math.ceil(filteredModels.length / pageSize);

  // Fetch models data
  const fetchModels = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/openrouter/models', {
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || `HTTP error ${response.status}`);
      }

      if (data.success) {
        setModels(data.models || []);
      } else {
        throw new Error(data?.error || 'Failed to fetch models');
      }
    } catch (err) {
      setError(err.message || 'Failed to load models');
      setModels([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchModels();
    fetchUserModelSettings();
  }, [fetchModels, fetchUserModelSettings]);

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchText, capabilityFilters]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(Number(size));
    setCurrentPage(1);
  };

  const handleToggleFavorite = async (modelId) => {
    // Prevent multiple clicks while updating
    if (updatingFavorites[modelId]) {
      return;
    }

    const currentFavorite = favoritedModels[modelId] || false;
    const newFavorite = !currentFavorite;

    // Optimistic update - immediately update UI
    setFavoritedModels(prev => ({
      ...prev,
      [modelId]: newFavorite
    }));

    try {
      await updateModelFavorite(modelId, newFavorite);
      // Success - optimistic update is kept
    } catch (error) {
      // Error handled in updateModelFavorite (reverts optimistic update)
    }
  };


  return (
    <div className="account-card settings-card models-card">
      <div className="card-header">
        <h2 className="card-title">
          <i className="fa-solid fa-brain"></i> AI Models
        </h2>
      </div>
      <div className="card-body models-body">
        <div className="models-content-layout">
          {/* Sidebar - Model Capabilities */}
          <div className="models-sidebar">
            <div className="sidebar-header">
              <h4>Model Filters</h4>
              <button
                className="clear-filters-btn"
                onClick={() => setCapabilityFilters({})}
              >
                Clear All
              </button>
            </div>
            <ModelCapabilities
              capabilityFilters={capabilityFilters}
              onCapabilityChange={setCapabilityFilters}
            />
          </div>

          {/* Main Content Area */}
          <div className="models-main-content">
            {/* Combined Search and Sorting Controls */}
            <div className="models-controls">
              <div className="controls-row">
                <div className="search-input-group">
                  <input
                    id="model-search"
                    type="text"
                    placeholder="Search models..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    className="models-search-input"
                  />
                </div>

                <div className="sort-by-group">
                  <DropdownSelect
                    value={sortBy}
                    onValueChange={(value) => {
                      setSortBy(value);
                      // Set default direction based on sort type
                      // Favorite should be descending (favorites first), others ascending
                      setSortDirection(value === "favorite" ? "desc" : "asc");
                    }}
                    options={[
                      { value: "none", label: "No Sorting" },
                      { value: "name", label: "Name" },
                      { value: "provider", label: "Provider" },
                      { value: "context", label: "Context Length" },
                      { value: "freeTier", label: "Free Tier" },
                      { value: "favorite", label: "Favorite" },
                      { value: "priceInput", label: "Input Price" },
                      { value: "priceOutput", label: "Output Price" },
                      { value: "priceBoth", label: "Avg Price" },
                    ]}
                    placeholder="Sort by..."
                    className="sort-select"
                  />
                </div>

                {sortBy !== "none" && (
                  <div className="sort-direction-group">
                    <button
                      className={`direction-toggle ${sortDirection === "asc" ? "active" : ""}`}
                      onClick={() => setSortDirection(sortDirection === "asc" ? "desc" : "asc")}
                      title={sortDirection === "asc" ? "Ascending" : "Descending"}
                    >
                      {sortDirection === "asc" ? "↑" : "↓"}
                    </button>
                  </div>
                )}
              </div>
            </div>


            {/* Models Table */}
            <div className="models-table-container">
          {loading ? (
            <div className="models-loading" role="status" aria-live="polite">
              <div className="loading-spinner models-loading-spinner" />
              <div className="models-loading-text">Loading models...</div>
            </div>
          ) : error ? (
            <div className="models-error">
              <p>Failed to load models: {error}</p>
              <button onClick={() => window.location.reload()}>Retry</button>
            </div>
          ) : (
            <RevoltTable
              className="scrollable"
              headers={['Provider', 'Name', 'Capabilities', 'Context Length', 'Input $/M', 'Output $/M', 'Favorite']}
              gridColumns={[0.4, 0.8, 0.6, 0.4, 0.3, 0.3, 0.2]}
              emptyMessage="No models found matching your criteria."
              rows={getFilteredModels()
                .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                .map((model) => [
                <span className={`model-provider-cell ${model.provider?.toLowerCase() || 'unknown'}`}>
                  {model.provider || 'Unknown'}
                </span>,
                <span className="model-name-cell">{model.name || model.id}</span>,
                <span className="model-capabilities-cell" title={model.detectedCapabilities?.join(', ') || ''}>
                  {model.detectedCapabilities && model.detectedCapabilities.length > 0
                    ? model.detectedCapabilities.slice(0, 2).join(', ') + (model.detectedCapabilities.length > 2 ? ` +${model.detectedCapabilities.length - 2} more` : '')
                    : 'None'
                  }
                </span>,
                <span className="model-context-cell">
                  {model.context_length ? model.context_length.toLocaleString() : '—'}
                </span>,
                <span className="model-price-cell">
                  {model.pricingNumeric?.prompt ? `$${(model.pricingNumeric.prompt * 1000000).toFixed(2)}` : '—'}
                </span>,
                <span className="model-price-cell">
                  {model.pricingNumeric?.completion ? `$${(model.pricingNumeric.completion * 1000000).toFixed(2)}` : '—'}
                </span>,
                <RevoltTableCell className="model-favorite-cell">
                  <div className="favorite-switch-container">
                <RevoltSwitch
                  checked={favoritedModels[model.id] || false}
                  onChange={() => handleToggleFavorite(model.id)}
                  disabled={updatingFavorites[model.id] || false}
                  className="favorite-switch"
                />
                  </div>
                </RevoltTableCell>
              ])}
            />
          )}
        </div>

        {/* Pagination Controls with Stats */}
        {!loading && !error && (
          <div className="models-pagination">
            <div className="page-size">
              <span>Show</span>
              <DropdownSelect
                value={pageSize}
                options={[10, 20, 50, 100]}
                onValueChange={(val) => handlePageSizeChange(Number(val))}
              />
              <span>per page</span>
            </div>

            {/* Models Stats - moved into pagination center */}
            <div className="models-stats">
              <span className="models-count">
                {filteredModels.length} of {models.length} model
                {models.length !== 1 ? "s" : ""}
                {(searchText.trim() ||
                  Object.keys(capabilityFilters).some(
                    (key) => capabilityFilters[key].enabled
                  )) &&
                  " (filtered)"}
              </span>
            </div>

            {/* Page controls - only show if multiple pages */}
            {filteredModels.length > pageSize && (
              <div className="page-controls">
                <button
                  className="page-btn"
                  onClick={() =>
                    handlePageChange(Math.max(1, currentPage - 1))
                  }
                  disabled={currentPage <= 1}
                >
                  <i className="fa-solid fa-chevron-left"></i>
                </button>
                <span className="page-indicator">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  className="page-btn"
                  onClick={() =>
                    handlePageChange(Math.min(totalPages, currentPage + 1))
                  }
                  disabled={currentPage >= totalPages}
                >
                  <i className="fa-solid fa-chevron-right"></i>
                </button>
              </div>
            )}
          </div>
        )}
          </div>
        </div>
      </div>
      <style>{`
        .models-body {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .models-content-layout {
          display: flex;
          gap: 20px;
          align-items: flex-start;
        }

        .models-sidebar {
          flex: 0 0 210px;
          min-width: 200px;
          max-width: 220px;
        }

        .models-main-content {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .sidebar-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .sidebar-header h4 {
          margin: 0;
          color: #f5f5f7;
          font-size: 1rem;
          font-weight: 600;
        }

        /* Table cell text overflow handling */
        .models-table-container .revolt-table-cell {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          text-align: center;
        }

        .models-table-container .revolt-table-cell span {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          display: block;
          text-align: center;
        }

        .models-controls {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .controls-row {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .search-input-group {
          flex: 1;
          min-width: 200px;
        }

        .models-search-input {
          background: rgba(255, 255, 255, 0.06);
          border: 1px solid rgba(255, 255, 255, 0.12);
          color: #f5f5f7;
          border-radius: 6px;
          padding: 8px 12px;
          font-size: 0.9rem;
          width:100%
        }

        .models-search-input:focus {
          outline: none;
          border-color: rgba(59, 130, 246, 0.5);
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
        }

        .search-input-group label {
          font-size: 0.85rem;
          color: #ccc;
          font-weight: 500;
        }

        .models-select {
          background: rgba(255, 255, 255, 0.06);
          border: 1px solid rgba(255, 255, 255, 0.12);
          color: #f5f5f7;
          border-radius: 6px;
          padding: 8px 12px;
        }

        .models-filters {
          background: rgba(20, 20, 20, 0.4);
          border: 1px solid rgba(139, 0, 0, 0.2);
          border-radius: 8px;
          padding: 16px;
        }

        .models-filters-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .models-filters h4 {
          color: #f5f5f7;
          margin: 0;
          font-size: 1rem;
          font-weight: 600;
        }

        .clear-filters-btn {
          background: rgba(255, 255, 255, 0.06);
          border: 1px solid rgba(255, 255, 255, 0.12);
          color: #f5f5f7;
          border-radius: 6px;
          padding: 6px 12px;
          font-size: 0.8rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .clear-filters-btn:hover {
          border-color: rgba(255, 255, 255, 0.25);
          background: rgba(255, 255, 255, 0.08);
        }

        .models-sorting {
          margin-top: 16px;
          margin-bottom: 16px;
        }



        .sort-by-group {
          flex: 0 0 180px;
        }

        .sort-direction-group {
          flex: 0 0 auto;
          width: 44px;
        }

        .sort-by-group label, .sort-direction-group label {
          font-size: 0.85rem;
          color: #ccc;
          font-weight: 500;
        }

        .sort-select {
          background: rgba(255, 255, 255, 0.06);
          border: 1px solid rgba(255, 255, 255, 0.12);
          color: #f5f5f7;
          border-radius: 6px;
          padding: 8px 12px;
        }


        .models-filters p {
          color: #ccc;
          margin: 0;
          font-size: 0.9rem;
        }

        /* Table container - constrains table height, table handles scrolling */
        .models-table-container {
          max-height: 400px;
          width: 100%;
          flex-shrink: 0;
        }

        .models-loading {
          min-height: 240px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 12px;
          color: #c7ccd5;
          font-size: 0.95rem;
        }

        .models-loading-spinner {
          width: 32px;
          height: 32px;
          border-width: 3px;
        }

        .models-loading-text {
          color: #c7ccd5;
        }

        .models-table-container .revolt-table {
          width: 100%;
          max-width: 100%;
        }

        .models-table-container .revolt-table.scrollable .revolt-table-body {
          max-height: 350px; /* Account for header height in 400px container */
        }

        .model-name-cell {
          font-weight: 600;
          color: #fff;
        }

        .model-provider-cell {
          padding: 2px 8px;
          background: rgba(139, 0, 0, 0.2);
          border: 1px solid rgba(139, 0, 0, 0.3);
          border-radius: 12px;
          color: #fff;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          display: inline-block;
        }


        .model-context-cell {
          color: #ccc;
          font-size: 0.85rem;
          text-align: right;
          font-family: monospace;
        }

        .model-capabilities-cell {
          color: #ccc;
          font-size: 0.85rem;
          text-align: left;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .model-favorite-cell {
          text-align: center;
        }

        .favorite-switch-container {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 100%;
        }

        .favorite-switch {
          transform: scale(0.8);
        }

        .models-pagination {
          margin-top: 8px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }

        .models-pagination .page-size {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          color: #cfd3db;
          font-size: 0.9rem;
          white-space: nowrap;
        }

        .models-pagination .page-size select {
          background: rgba(255, 255, 255, 0.06);
          border: 1px solid rgba(255, 255, 255, 0.12);
          color: #f5f5f7;
          border-radius: 6px;
          padding: 4px 8px;
        }

        .models-pagination .page-controls {
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }

        .page-btn {
          background: rgba(255, 255, 255, 0.06);
          color: #f5f5f7;
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 6px;
          padding: 6px 10px;
          cursor: pointer;
          transition: transform 0.2s ease, border-color 0.2s ease;
        }

        .page-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .page-btn:not(:disabled):hover {
          border-color: rgba(255, 255, 255, 0.25);
        }

        .page-btn:not(:disabled):active {
          transform: translateY(1px);
        }

        .models-pagination .page-indicator {
          color: #cfd3db;
          font-size: 0.9rem;
        }

        /* Models stats in pagination center */
        .models-pagination .models-stats {
          margin-top: 0;
          flex: 1;
          text-align: center;
          min-width: 0;
          color: #cfd3db;
        }

        .models-stats {
          margin-top: 8px;
          color: #ccc;
          font-size: 0.9rem;
        }

        .models-count {
          font-weight: 500;
        }

        /* Direction toggle button (icon-only) */
        .direction-toggle {
          background: rgba(255, 255, 255, 0.06);
          border: 1px solid rgba(255, 255, 255, 0.12);
          color: #f5f5f7;
          border-radius: 6px;
          padding: 0;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
        }

        .direction-toggle:hover {
          border-color: rgba(255, 255, 255, 0.25);
          background: rgba(255, 255, 255, 0.08);
        }

        .direction-toggle.active {
          background: rgba(59, 130, 246, 0.1);
          border-color: rgba(59, 130, 246, 0.5);
        }
      `}</style>
    </div>
  );
}
